/**
 * Date formatting utilities with Pacific Standard Time
 * 
 * MongoDB stores all datetimes in UTC but Motor returns them as naive (no timezone).
 * Pydantic serializes them without timezone suffix (e.g., "2026-03-19T23:00:00").
 * JavaScript's new Date() treats naive strings as LOCAL time, not UTC.
 * This helper ensures naive datetime strings are treated as UTC before conversion to Pacific.
 */

const TIMEZONE = 'America/Los_Angeles';

/**
 * Parse a date value ensuring naive datetime strings are treated as UTC.
 * MongoDB returns UTC datetimes without timezone info - we must handle this.
 */
const toUTCDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) return date;
  const str = String(date);
  if (!str.endsWith('Z') && !str.includes('+') && !str.includes('-', 10)) {
    return new Date(str + 'Z');
  }
  return new Date(str);
};

/**
 * Format date to Pacific Time with full date and time
 */
export const formatDateTime = (date) => {
  const d = toUTCDate(date);
  if (!d) return '';
  return d.toLocaleString('en-US', {
    timeZone: TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format date to Pacific Time with short date and time (no year)
 */
export const formatDateTimeShort = (date) => {
  const d = toUTCDate(date);
  if (!d) return '';
  return d.toLocaleString('en-US', {
    timeZone: TIMEZONE,
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format date to Pacific Time (date only)
 */
export const formatDate = (date) => {
  const d = toUTCDate(date);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    timeZone: TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format date to Pacific Time (date only, no year)
 */
export const formatDateShort = (date) => {
  const d = toUTCDate(date);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    timeZone: TIMEZONE,
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format time to Pacific Time (time only)
 */
export const formatTime = (date) => {
  const d = toUTCDate(date);
  if (!d) return '';
  return d.toLocaleTimeString('en-US', {
    timeZone: TIMEZONE,
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format date for display with weekday
 */
export const formatDateWithWeekday = (date) => {
  const d = toUTCDate(date);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    timeZone: TIMEZONE,
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};
